import { agentGlobalToolRows } from 'utils/agentGlobalTools';

/**
 * Union of tools attached on skills (read-only view at agent level).
 */

function collectAttachments(skills) {
  const dbTools = [];
  const builtins = [];
  const skillsByTool = new Map();
  const skillsByBuiltin = new Map();

  (skills || []).forEach(skill => {
    (skill.attachedTools || []).forEach(att => {
      if (att?.tool) {
        const id = String(att.tool);
        if (!skillsByTool.has(id)) skillsByTool.set(id, []);
        skillsByTool.get(id).push(skill);
        const existing = dbTools.find(t => String(t.tool) === id);
        if (!existing) {
          dbTools.push({ tool: id, usage: att.usage || '', skills: [skill] });
        } else if (!existing.skills.includes(skill)) {
          existing.skills.push(skill);
        }
      } else if (att?.builtin) {
        const key = att.builtin;
        if (!skillsByBuiltin.has(key)) skillsByBuiltin.set(key, []);
        skillsByBuiltin.get(key).push(skill);
        const existing = builtins.find(b => b.builtin === key);
        if (!existing) {
          builtins.push({ builtin: key, usage: att.usage || '', skills: [skill] });
        } else if (!existing.skills.includes(skill)) {
          existing.skills.push(skill);
        }
      }
    });
  });

  return { dbTools, builtins, skillsByTool, skillsByBuiltin };
}

/**
 * @param {object[]} attachedSkills - skills linked on the agent
 * @param {object[]} allTools - company tool catalog
 */
export function unionToolsFromSkills(attachedSkills, allTools = []) {
  const { dbTools, builtins, skillsByTool } = collectAttachments(attachedSkills);
  const toolById = new Map((allTools || []).map(t => [String(t._id), t]));

  const tools = dbTools
    .map(({ tool, usage, skills }) => {
      const doc = toolById.get(String(tool));
      if (!doc) return null;
      return {
        ...doc,
        usage,
        skillsForTool: skillsByTool.get(String(tool)) || skills,
      };
    })
    .filter(Boolean);

  const builtinRows = builtins.map(({ builtin, usage, skills }) => ({
    id: `builtin:${builtin}`,
    _id: `builtin:${builtin}`,
    name: builtin,
    description: usage,
    kind: 'builtin',
    builtin: true,
    skillsForTool: skills,
  }));

  return {
    tools,
    builtins: builtinRows,
    rows: [...builtinRows, ...tools],
    skillsByTool,
  };
}

export function attachedSkillsForAgent(agent, skills) {
  const ids = (agent?.skillIds || []).map(String);
  return ids.map(id => skills.find(s => String(s._id) === id)).filter(Boolean);
}

/**
 * Union of knowledge attached on skills (read-only view at agent level).
 */
export function unionKnowledgeFromSkills(attachedSkills, allKnowledge = []) {
  const skillsByKnowledge = new Map();
  (attachedSkills || []).forEach(skill => {
    (skill.attachedKnowledge || []).forEach(att => {
      if (!att?.knowledge) return;
      const id = String(att.knowledge);
      if (!skillsByKnowledge.has(id)) skillsByKnowledge.set(id, []);
      const list = skillsByKnowledge.get(id);
      if (!list.includes(skill)) list.push(skill);
    });
  });

  const knowledgeById = new Map((allKnowledge || []).map(k => [String(k._id), k]));
  const knowledge = [...skillsByKnowledge.keys()]
    .map(id => {
      const doc = knowledgeById.get(id);
      if (!doc) return null;
      return {
        ...doc,
        skillsForKnowledge: skillsByKnowledge.get(id) || [],
      };
    })
    .filter(Boolean)
    .sort((a, b) =>
      String(a.displayName || a.name || '').localeCompare(String(b.displayName || b.name || '')),
    );

  return { knowledge, rows: knowledge, skillsByKnowledge };
}

export function unionKnowledgeForAgent(agent, skills, allKnowledge = []) {
  const attachedSkills = attachedSkillsForAgent(agent, skills);
  return unionKnowledgeFromSkills(attachedSkills, allKnowledge);
}

/**
 * Company knowledge catalog for the Knowledge sidebar and panel.
 * Includes bases that are not attached to any skill.
 */
export function catalogKnowledgeRows(allKnowledge = [], skills = []) {
  const { skillsByKnowledge } = unionKnowledgeFromSkills(skills, allKnowledge);
  return [...(allKnowledge || [])]
    .map(k => ({
      ...k,
      skillsForKnowledge: skillsByKnowledge.get(String(k._id)) || [],
    }))
    .sort((a, b) =>
      String(a.displayName || a.name || '').localeCompare(String(b.displayName || b.name || '')),
    );
}

/**
 * All tools reachable by an agent: skill attachments plus agent-level lifecycle tools.
 */
export function unionToolsForAgent(agent, attachedSkills, allTools = []) {
  const { rows: skillRows } = unionToolsFromSkills(attachedSkills, allTools);
  const globalRows = agentGlobalToolRows(agent, allTools);
  const byId = new Map();

  skillRows.forEach(row => {
    byId.set(String(row._id), { ...row, agentGlobal: false });
  });

  globalRows.forEach(row => {
    const id = String(row._id);
    const globalRow = {
      ...row,
      skillsForTool: [],
      agentGlobal: true,
    };
    const existing = byId.get(id);
    if (existing) {
      byId.set(id, {
        ...existing,
        agentGlobal: true,
        trigger: globalRow.trigger || existing.trigger,
        usage: globalRow.usage || existing.usage,
      });
    } else {
      byId.set(id, globalRow);
    }
  });

  const rows = Array.from(byId.values()).sort((a, b) =>
    String(a.name || '').localeCompare(String(b.name || '')),
  );
  const tools = rows.filter(r => !r.builtin);
  const builtins = rows.filter(r => r.builtin);

  return { tools, builtins, rows };
}
